import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Accordion from 'components/Accordion';
import AccordionItem from 'components/AccordionItem';
import Link from 'components/Link';
import { LogIn } from 'components/Icons';
import { HeaderContext } from '../../headerContext';
import { px2rem } from '../../../../utilities/UnitHelper/unitHelper';

const StyledAccordion = styled(Accordion)`
  border: 0;
  box-sizing: border-box;

  /* accordionitem */
  div {
    border: 0;

    button,
    button:first-child {
      ${(props) => props.theme.typography.bodyBold};
      min-height: 3rem;
      width: 100%;
    }

    &:focus:enabled {
      border-bottom: 2px solid ${(props) => props.theme.colors.grayscale.black};
    }

    & div.collapsible-content > div {
      border-top: 2px solid black;
      margin: 0;
      padding: 0;
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .isOpened {
      & button,
      & a {
        color: ${(props) => props.theme.colors.grayscale.black};
      }

      svg {
        margin-right: 0.5rem;

        & path {
          fill: ${(props) => props.theme.colors.grayscale.black};
        }
      }
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        background: ${(props) => props.theme.colors.grayscale.black};
        padding: 0.532rem 0;
        border-bottom: solid 1px
          ${(props) => props.theme.colors.grayscale.white};

        &:last-child {
          margin-bottom: 0;
        }

        a,
        a:last-child {
          border: none;
          ${(props) => props.theme.typography.body};
          color: ${(props) => props.theme.colors.grayscale.white};
          min-height: auto;
        }
      }
    }
  }

  button,
  a {
    ${(props) => props.theme.typography.button}
    color: ${(props) => props.theme.colors.grayscale.black};
    border-bottom: solid 1px ${(props) => props.theme.colors.grayscale.gray3};
    padding: 0.25rem 1rem 0.25rem ${px2rem(55)};

    svg {
      & path {
        fill: ${(props) => props.theme.colors.grayscale.gray0};
      }
    }
  }

  a {
    cursor: pointer;
    display: block;
    line-height: 3rem;
    text-decoration: none;

    &:last-child {
      align-items: center;
      display: flex;
      line-height: inherit;
      min-height: 3rem;

      span {
        margin-right: 0.5rem;
      }
    }
  }

  a:hover,
  button:hover {
    color: ${(props) => props.theme.colors.grayscale.black};

    svg {
      & path {
        fill: ${(props) => props.theme.colors.grayscale.gray0};
      }
    }
  }
`;

StyledAccordion.displayName = 'Accordion';

/**
 * renders an accordion item for the my account (userbadge)
 * @param {object} list the list of items to render
 * @param {string} logOutUrl
 * @param {Function} the link wrapper component
 * @param {Function} the onclick handler for the link
 */
function renderAccordionItem(list, logOutUrl, linkWrapper, handleClick) {
  const accordionArray = [];

  list.forEach((listItem) => {
    const id = listItem.id || listItem.text.replace(/ /, '-');

    accordionArray.push(
      <li key={`mobile-${id}-item`}>
        <Link
          id={`mobile-${id}`}
          href={listItem.url}
          LinkWrapper={linkWrapper}
          onClick={handleClick}
          key={`mobile-${id}-link`}
        >
          {listItem.text}
        </Link>
      </li>
    );
  });

  accordionArray.push(
    <li key="mobile-logout-item">
      <Link
        id="mobile-logout"
        href={logOutUrl}
        LinkWrapper={linkWrapper}
        onClick={handleClick}
        key="mobile-logout-link"
      >
        <span>Log Out</span>
      </Link>
    </li>
  );

  return (
    <AccordionItem id="mobile-my-account-acc" title="My Account">
      <ul key="mobile-my-account-list">{accordionArray}</ul>
    </AccordionItem>
  );
}
/**
 * Nav that contains a list of links to be displayed
 * and handles the active state via URLs.
 */
function MobileNav({ onClick, logInUrl, logOutUrl }) {
  const headerContext = useContext(HeaderContext);
  const { navItems, LinkWrapper, userBadgeProps } = headerContext;
  const { user, urlList } = userBadgeProps || { user: '', urlList: [] };

  /**
   * Provides a click event handler for the nav items.
   */
  const handleClick = () => {
    if (typeof onClick === 'function') {
      onClick();
      return;
    }

    console.error('onClick supplied to mobileNav is not a function');
  };

  /**
   * Builds a nav/subnav recursively
   *
   * @param {Array} navItems the array of nav items
   * @returns {Array} menuItems
   */
  const buildMenu = (items) => {
    const menuItems = [];

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.subNav) {
        /* eslint-disable-next-line no-loop-func */
        const subNav = item.subNav.map((subNavItem) => (
          <li key={`mobile-${subNavItem.id}-item`}>
            <Link
              id={`mobile-${subNavItem.id}`}
              href={subNavItem.url}
              data-subnav={false}
              onClick={handleClick}
              LinkWrapper={LinkWrapper}
              key={`mobile-${item.id}-link`}
            >
              {subNavItem.name}
            </Link>
          </li>
        ));
        menuItems.push(
          <AccordionItem
            title={item.name}
            id={`mobile-${item.id}`}
            key={`mobile-${item.id}-acc`}
          >
            <ul key={`mobile-${item.id}-list`}>{subNav}</ul>
          </AccordionItem>
        );
      } else {
        menuItems.push(
          <Link
            id={`mobile-${item.id}`}
            href={item.url}
            data-subnav={false}
            LinkWrapper={LinkWrapper}
            key={`mobile-${item.id}-link`}
          >
            {item.name}
          </Link>
        );
      }
    }
    return menuItems;
  };

  const menu = navItems ? buildMenu(navItems) : null;

  return (
    <StyledAccordion>
      {menu}
      {user ? (
        renderAccordionItem(urlList, logOutUrl, LinkWrapper, handleClick)
      ) : (
        <Link id="mobile-login" href={logInUrl} LinkWrapper={LinkWrapper}>
          <span>Log In | Sign Up</span>
          <LogIn />
        </Link>
      )}
    </StyledAccordion>
  );
}

/**
 * propTypes for this component
 */
MobileNav.propTypes = {
  /**
   *  Fires when user click on an item within the user badge.
   */
  onClick: PropTypes.func,
  /**
   * Log in url
   */
  logInUrl: PropTypes.string,
  logOutUrl: PropTypes.string,
};

/**
 * Default prop types
 */
MobileNav.defaultProps = {
  logInUrl: '/signin',
  logOutUrl: '/logout',
  onClick: () => {},
};

/* @component */
export default MobileNav;
